import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment.dev';

/**
 * Interface for Microsoft Azure API responses
 */
export interface MicrosoftAzureResponse<T = any> {
  success: boolean;
  message: string;
  data: T;
}

/**
 * Interface for consent URL response
 */
export interface ConsentUrlData {
  consentUrl: string;
}

/**
 * Service for managing Microsoft Azure SSO integration
 * Handles OAuth consent flow and Azure AD user management
 */
@Injectable({
  providedIn: 'root'
})
export class MicrosoftAzureService {

  /**
   * Base URL for Microsoft Azure API endpoints
   */
  private apiUrl = `${environment.apiUrl}/microsoft-azure`;

  constructor(private http: HttpClient) { }

  /**
   * Get the Microsoft OAuth consent URL for SSO authentication
   * Opens a popup window for the user to grant consent
   * @param frontendCallbackUrl - Frontend callback URL where Microsoft will redirect after consent
   * @returns Observable with the consent URL
   */
  getConsentUrl(frontendCallbackUrl: string): Observable<MicrosoftAzureResponse<ConsentUrlData>> {
    return this.http.get<MicrosoftAzureResponse<ConsentUrlData>>(`${this.apiUrl}/consent-url`, {
      params: { frontendCallbackUrl }
    });
  }

  /**
   * Get all Azure Active Directory users for the organization
   * Requires proper consent and permissions to be granted
   * @param nextLink - Optional next link for pagination
   * @param searchQuery - Optional search query to filter users by name
   * @returns Observable with the list of Azure AD users
   */
  getAzureUsers(nextLink?: string, searchQuery?: string): Observable<MicrosoftAzureResponse<{ value: any[], totalCount: number, nextLink?: string, disconnected: true }>> {
    const params: any = {};
    if (nextLink) {
      params.nextLink = nextLink;
    }
    if (searchQuery && searchQuery.trim()) {
      params.search = searchQuery.trim();
    }
    return this.http.get<MicrosoftAzureResponse<{ value: any[], totalCount: number, nextLink?: string, disconnected: true }>>(`${this.apiUrl}/users`, { params });
  }

  /**
   * Exchange authorization code for access token
   * Called after Microsoft redirects back with auth code
   * @param code - Authorization code from OAuth redirect
   * @param state - Organization ID from OAuth redirect
   * @param frontendCallbackUrl - Frontend callback URL (must match the one used in consent)
   * @returns Observable with success response
   */
  exchangeCodeForToken(code: string, state: string, frontendCallbackUrl: string): Observable<MicrosoftAzureResponse<any>> {
    return this.http.post<MicrosoftAzureResponse<any>>(`${this.apiUrl}/callback`, {
      code,
      state,
      frontendCallbackUrl
    });
  }

  /**
   * Disconnect Microsoft Azure integration
   * Revokes consent and clears all stored tokens
   * @returns Observable with success response
   */
  disconnectAzure(): Observable<MicrosoftAzureResponse<any>> {
    return this.http.post<MicrosoftAzureResponse<any>>(`${this.apiUrl}/disconnect`, {});
  }

  /**
   * Open Microsoft consent in a popup window
   * Handles the OAuth flow in a separate window
   * Uses postMessage API for reliable cross-origin communication
   * @param consentUrl The Microsoft OAuth authorization URL
   * @returns Promise that resolves when consent is granted or rejects on error
   */
  openConsentPopup(consentUrl: string): Promise<any> {
    return new Promise((resolve, reject) => {
      // Open popup window with Microsoft OAuth URL
      const width = 600;
      const height = 700;
      const left = (screen.width / 2) - (width / 2);
      const top = (screen.height / 2) - (height / 2);

      const popup = window.open(
        consentUrl,
        'Microsoft Azure Consent',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
      );

      if (!popup) {
        reject(new Error('Popup blocked. Please allow popups for this site.'));
        return;
      }

      // Message handler for postMessage communication
      const messageHandler = (event: MessageEvent) => {
        // Verify origin for security
        if (event.origin !== window.location.origin) {
          return;
        }

        if (event.data && event.data.type === 'MICROSOFT_CONSENT_SUCCESS') {
          window.removeEventListener('message', messageHandler);
          clearInterval(pollTimer);
          clearTimeout(timeoutId);
          sessionStorage.removeItem('microsoftConsentStatus');
          resolve({ success: true });
        } else if (event.data && event.data.type === 'MICROSOFT_CONSENT_ERROR') {
          window.removeEventListener('message', messageHandler);
          clearInterval(pollTimer);
          clearTimeout(timeoutId);
          sessionStorage.removeItem('microsoftConsentStatus');
          reject(new Error(event.data.message || 'Consent failed'));
        }
      };

      // Listen for postMessage from popup
      window.addEventListener('message', messageHandler);

      // Fallback: Poll for popup closure and sessionStorage status
      // This handles cases where postMessage might not work
      let popupClosed = false;
      const pollTimer = setInterval(() => {
        try {
          // Check if popup is closed (may throw COOP error, so catch it)
          const isClosed = popup.closed;
          if (isClosed && !popupClosed) {
            popupClosed = true;
            // Give a small delay to allow sessionStorage to be set
            setTimeout(() => {
              const consentStatus = sessionStorage.getItem('microsoftConsentStatus');
              if (consentStatus === 'success') {
                window.removeEventListener('message', messageHandler);
                clearInterval(pollTimer);
                clearTimeout(timeoutId);
                sessionStorage.removeItem('microsoftConsentStatus');
                resolve({ success: true });
              } else {
                // Only reject if we haven't already resolved via postMessage
                window.removeEventListener('message', messageHandler);
                clearInterval(pollTimer);
                clearTimeout(timeoutId);
                reject(new Error('Consent process was cancelled. Please try again to complete the connection.'));
              }
            }, 1000);
          }
        } catch (e) {
          // COOP error - popup navigated to different origin
          // Continue polling sessionStorage as fallback
          const consentStatus = sessionStorage.getItem('microsoftConsentStatus');
          if (consentStatus === 'success') {
            window.removeEventListener('message', messageHandler);
            clearInterval(pollTimer);
            clearTimeout(timeoutId);
            sessionStorage.removeItem('microsoftConsentStatus');
            resolve({ success: true });
          }
        }
      }, 500);

      // Set timeout for consent process
      const timeoutId = setTimeout(() => {
        try {
          if (!popup.closed) {
            popup.close();
          }
        } catch (e) {
          // Ignore COOP errors when closing
        }
        window.removeEventListener('message', messageHandler);
        clearInterval(pollTimer);
        reject(new Error('Consent timeout - please try again'));
      }, 300000); // 5 minute timeout
    });
  }
}

